import type { JSX } from "react";
import type { ArtifactBlock } from "@shared/types";
import type { BlockRendererProps } from "./registry";
import { IconFile } from "../../icons";
import { artifactApi, SURFACE_LABEL } from "../../../lib/artifactApi";
import "../../../styles/artifacts.css";

/**
 * Artifact-card renderer (FEATURE_ARTIFACTS · task #7 · Phase 7a). Renders the
 * in-chat card for a deliverable GENERATED this turn and PERSISTED as a
 * first-class object (design mockup ①): a faux-document thumbnail, the title with
 * a 「生成」badge (distinguishing generated from uploaded 我的资料), and open /
 * download actions. The artifact already lives in the Library (the host persisted
 * it on create), so there is no separate save step; "打开" opens the side canvas,
 * "下载" saves the markdown locally. Promote-to-knowledge is Phase 7b.
 */
export function ArtifactCardView({ block, onOpenArtifact }: BlockRendererProps): JSX.Element {
  const artifact = block as ArtifactBlock;

  /** Download the artifact's current markdown as a .md file. Fetches the full
   *  content on demand (the block carries only id/title). */
  const onDownload = async (): Promise<void> => {
    const detail = await artifactApi.get(artifact.artifactId);
    if (detail === null) return;
    const blob = new Blob([detail.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${detail.title || "文档"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="artifact-card" data-testid="artifact-card">
      <div className="artifact-card-thumb" aria-hidden="true">
        <span className="artifact-card-ic">
          <IconFile size={18} />
        </span>
        <span className="artifact-card-lines">
          <i style={{ width: "60%" }} />
          <i style={{ width: "90%" }} />
          <i style={{ width: "78%" }} />
        </span>
      </div>
      <div className="artifact-card-body">
        <div className="artifact-card-title">
          <span className="artifact-card-name">{artifact.title}</span>
          <span className="artifact-badge artifact-badge-gen">生成</span>
        </div>
        <div className="artifact-card-meta">{SURFACE_LABEL[artifact.surface]} · 来自本次对话</div>
        <div className="artifact-card-acts">
          {onOpenArtifact !== undefined && (
            <button
              type="button"
              className="artifact-btn artifact-btn-pri"
              data-testid="artifact-card-open"
              onClick={() => onOpenArtifact(artifact.artifactId)}
            >
              打开
            </button>
          )}
          <button
            type="button"
            className="artifact-btn"
            data-testid="artifact-card-download"
            onClick={() => void onDownload()}
          >
            下载
          </button>
        </div>
      </div>
    </div>
  );
}
